import React, { useEffect, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Dialog, NoContent, Table, Typography } from '../../../../ui-kit';
import {
    RossumUploadStatus,
    refetchRossumUploadsByWarehouseIdQuery,
    refetchRossumUploadsCountByWarehouseIdQuery,
    useDeleteRossumUploadMutation,
    useInvoiceStatusUpdatesSubscription,
    useRossumUploadsByWarehouseIdQuery,
} from '../../../../generated/graphql';
import { useWarehousesScope } from '../../../warehouse/providers/WarehouseScopeProvider';
import PendingDeliveryListTableHeadCell from './PendingDeliveryListsTableHeadCell';
import PendingDeliveryListsTableContent from './PendingDeliveryListsTableContent';
import { pendingDeliveryListsHeadCells } from './constants';
import { PendingDeliveryLists } from './types';

const PendingDeliveryListsTable = () => {
    const { t } = useTranslation();
    const noDeliveryListTitle = t('No delivery lists in process');
    const { currentWarehouseId } = useWarehousesScope();

    const [pendingDeliveryLists, setPendingDeliveryLists] = useState<PendingDeliveryLists>([]);
    const [deletingRossumUpload, setDeletingRossumUpload] = useState<{ rossumUploadId: string; filename: string } | null>(null);

    const { data: rossumUploads, loading } = useRossumUploadsByWarehouseIdQuery({
        variables: { warehouseId: currentWarehouseId as string }
    });

    const [deleteRossumUpload] = useDeleteRossumUploadMutation({
        refetchQueries: [
            refetchRossumUploadsByWarehouseIdQuery({ warehouseId: currentWarehouseId as string }),
            refetchRossumUploadsCountByWarehouseIdQuery({ warehouseId: currentWarehouseId as string }),
        ],
    });

    const sortAndSetPendingDeliveryLists = (entries: PendingDeliveryLists) => {
        const sortedEntries = [...entries].sort((a, b) => {
            if (a.status === RossumUploadStatus.Confirmed && b.status !== RossumUploadStatus.Confirmed) {
                return -1;
            }

            if (b.status === RossumUploadStatus.Confirmed && a.status !== RossumUploadStatus.Confirmed) {
                return 1;
            }

            return a.createdAtUtc > b.createdAtUtc ? 1 : -1;
        });

        setPendingDeliveryLists(sortedEntries);
    };

    useInvoiceStatusUpdatesSubscription({
        variables: { warehouseId: currentWarehouseId as string },
        onSubscriptionData: ({ subscriptionData }) => {
            const { data } = subscriptionData;
            if (data?.invoiceStatusUpdates) {
                const { status, annotation } = data.invoiceStatusUpdates;

                const updatedPendingDeliveryLists = pendingDeliveryLists.map(list => ({
                    ...list,
                    status: annotation === list.rossumAnnotationUrl ? status : list.status,
                }));
                sortAndSetPendingDeliveryLists(updatedPendingDeliveryLists);
            }
        },
    });

    useEffect(() => {
        if (rossumUploads) {
            sortAndSetPendingDeliveryLists(rossumUploads.rossumUploadsByWarehouseId);
        }
    }, [rossumUploads]);

    const handleDelete = (rossumUploadId: string, filename: string) => {
        setDeletingRossumUpload({ rossumUploadId, filename });
    };

    const handleDeleteCancel = () => {
        setDeletingRossumUpload(null);
    };

    const handleDeleteConfirm = async () => {
        if (deletingRossumUpload) {
            const { rossumUploadId } = deletingRossumUpload;
            await deleteRossumUpload({ variables: { rossumUploadId } });
            setDeletingRossumUpload(null);
        }
    };

    if (!loading && pendingDeliveryLists && pendingDeliveryLists.length === 0) {
        return <NoContent title={noDeliveryListTitle} />;
    }

    return (
        <>
            <Grid container justify="center" alignItems="center">
                <Grid item xs={12}>
                    <Table
                        isFirstColumnSticky
                        headCells={pendingDeliveryListsHeadCells}
                        TableHeadCellComponent={PendingDeliveryListTableHeadCell}
                    >
                        <PendingDeliveryListsTableContent
                            pendingDeliveryLists={pendingDeliveryLists}
                            isLoading={loading}
                            onDelete={handleDelete}
                        />
                    </Table>
                </Grid>
            </Grid>
            <Dialog isOpened={!!deletingRossumUpload}>
                <DialogTitle>
                    {t('Confirm deletion')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant='body1'>
                        {t('Are you sure you want to delete')}
                        {' '}
                        {deletingRossumUpload?.filename}
                        ?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>{t('Cancel')}</Button>
                    <Button color='secondary' variant='contained' onClick={handleDeleteConfirm}>{t('Confirm')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PendingDeliveryListsTable;
